import type { ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Badge from "@/v2-backoffice-client/components/badge"
import { useAppVersionDistributionQuery } from "@/v2-backoffice-client/appVersion/distribution/appVersion.distribution.query"

export type AppVersionDistributionCardProps = ComponentProps<"div">

// Fleet-wide app-version overview for the Activity page: one "version · user
// count" badge per cohort version, plus a muted bucket for users with no
// version-stamped login yet. Decoration-shaped per the partial-data rule:
// degrades to nothing while loading, on error, or when no user has a
// versioned login yet.
function AppVersionDistributionCard(props: AppVersionDistributionCardProps) {
  const { className, ...divProps } = props

  const { data } = useAppVersionDistributionQuery()

  const buckets = data?.getAppVersionDistribution ?? []
  // The null bucket ("no versioned login yet") is only meaningful next to at
  // least one real version; on its own it would be a strip of pure noise.
  const versioned = buckets.filter((bucket) => bucket.AppVersion)
  if (versioned.length === 0) return null
  const unversioned = buckets.find((bucket) => !bucket.AppVersion)

  return (
    <div
      className={cn(
        "app-version-distribution-card flex flex-wrap items-center gap-1.5",
        className,
      )}
      {...divProps}
    >
      <span className="text-xs text-muted-foreground">App versions:</span>
      {versioned.map((bucket) => (
        <Badge
          key={bucket.AppVersion}
          variant="outline"
          title={`${bucket.UserCount} user(s) whose latest login runs ${bucket.AppVersion}`}
        >
          <span className="font-mono">{bucket.AppVersion}</span>
          <span className="ml-1 text-muted-foreground">{bucket.UserCount}</span>
        </Badge>
      ))}
      {unversioned && (
        <Badge
          variant="outline"
          className="text-muted-foreground"
          title="Users with no version-stamped login yet (no recent activity)"
        >
          No version
          <span className="ml-1">{unversioned.UserCount}</span>
        </Badge>
      )}
    </div>
  )
}

export default AppVersionDistributionCard
